import React, { useEffect, useRef, useState } from 'react';
import { InputMask } from '@react-input/mask';
import { IconUser } from '@tabler/icons-react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { fetchManagerOptions } from '../../../entities/user/model/api';
import { PHONE_MASK_OPTIONS } from '../../../shared/lib/phone';
import { CitySelect } from '../../../shared/ui/CitySelect';
import { DateInput } from '../../../shared/ui/DateInput';
import { FormField } from '../../../shared/ui/FormField';
import { Input } from '../../../shared/ui/Input';
import { Select } from '../../../shared/ui/Select';
import { buildPreview, MoscowFormValues, validators } from '../model/preview';
import { PinMoscowFormProps } from '../model/types';
import dayjs from 'dayjs';

const PASSPORT_MASK = { mask: '____ ______', replacement: { _: /\d/ } };
const SNILS_MASK = { mask: '___-___-___ __', replacement: { _: /\d/ } };

const yesNoOpts = (yes: string) => [{ value: 'yes', label: yes }, { value: 'no', label: 'Нет' }];

const defaults = (): MoscowFormValues => ({
  fullName: '', birthDate: '', phone: '', passport: '', snils: '', inn: '',
  departureCity: '', arrivalCity: 'Москва', desiredDate: '', health: '',
  militaryId: '', criminalRecord: '', relationNeeded: '',
  bFullName: '', bMaritalStatus: '', bEducation: '', bCriminalRecord: '', bPhone: '', bDepartment: '',
});

const PinMoscowForm: React.FC<PinMoscowFormProps> = ({
  prefill, departmentOptions, isSaving, onCancel, onSubmit,
}) => {
  const [ownerId, setOwnerId] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerError, setOwnerError] = useState('');

  // предзаполнение из карточки — берём один раз на маунт (prefill — новый объект
  // на каждом рендере родителя, через defaultValues его нельзя пересобирать).
  const initial = useRef<MoscowFormValues>();
  if (!initial.current) {
    initial.current = {
      ...defaults(),
      fullName: prefill.fullName ?? '',
      phone: prefill.phone ?? '',
      departureCity: prefill.departureCity ?? '',
      desiredDate: prefill.desiredDate ?? '',
    };
  }

  const {
    control, register, setValue,
    formState: { errors },
  } = useForm<MoscowFormValues>({
    mode: 'onTouched',
    shouldUnregister: true, // скрытый блок B не участвует в валидации
    defaultValues: initial.current!,
  });

  // useWatch гарантированно перерисовывает компонент на каждое изменение любого поля
  // (включая uncontrolled register-инпуты) → предпросмотр обновляется сразу.
  const v = useWatch({ control, defaultValue: initial.current }) as MoscowFormValues;
  const showRelation = v.relationNeeded === 'yes';

  // ── синхронизация B.1/B.4/B.5 с A, пока пользователь не тронул поле B вручную ──
  const bDirty = useRef({ fullName: false, criminal: false, phone: false });
  useEffect(() => { if (!bDirty.current.fullName) setValue('bFullName', v.fullName); }, [v.fullName, setValue]);
  useEffect(() => { if (!bDirty.current.criminal) setValue('bCriminalRecord', v.criminalRecord); }, [v.criminalRecord, setValue]);
  useEffect(() => { if (!bDirty.current.phone) setValue('bPhone', v.phone); }, [v.phone, setValue]);

  // Предпросмотр считаем прямо в рендере — обновляется мгновенно на каждое изменение.
  const preview = buildPreview(v);
  const canSubmit = !!ownerId && preview.tasks.length > 0;

  const todayStr = dayjs().format('YYYY-MM-DD');

  // Сабмит опирается на тот же buildPreview(v), что и превью — единый источник правды.
  // Через RHF handleSubmit не идём: он валидирует поля скрытого блока B (их подмешивает
  // useEffect setValue выше даже при «Отношение = нет») и молча блокирует сабмит.
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ownerId) { setOwnerError('Выберите администратора'); return; }
    if (preview.tasks.length === 0) return; // превью уже показывает, что заполнить
    await onSubmit(preview.tasks, ownerId);
  };

  const departmentsEmpty = departmentOptions.length === 0;

  return (
    <form className="pin-moscow" onSubmit={submit}>
      <div className="pin-moscow-body">
        {/* Администратор-исполнитель (нужен беку для маршрутизации задачи) */}
        <FormField className="pin-field" label="АДМИНИСТРАТОР (ИСПОЛНИТЕЛЬ)" labelClassName="pin-label" error={ownerError}>
          <Select
            name="pin-owner"
            placeholder="Выберите администратора"
            icon={<IconUser size={14} stroke={2} />}
            fetchOptions={fetchManagerOptions}
            options={ownerId && ownerName ? [{ value: ownerId, label: ownerName }] : []}
            value={ownerId}
            onChange={(val, opt) => { setOwnerId(val); setOwnerName(opt?.label ?? ''); setOwnerError(''); }}
          />
        </FormField>

        {/* ── Блок A ── */}
        <div className="pin-block-title">Полная анкета для закрепления в Москве</div>

        <div className="pin-grid">
          <FormField className="pin-field" label="1. ФАМИЛИЯ ИМЯ ОТЧЕСТВО" labelClassName="pin-label" error={errors.fullName?.message}>
            <Input className="inf-input" aria-invalid={!!errors.fullName}
              {...register('fullName', { validate: validators.fullName })} />
          </FormField>

          <FormField className="pin-field" label="2. ДАТА РОЖДЕНИЯ" labelClassName="pin-label" error={errors.birthDate?.message}>
            <input type="hidden" {...register('birthDate', { validate: validators.birthDate })} />
            <DateInput className="inf-input" name="birthDate" max={todayStr} value={v.birthDate}
              onChange={e => setValue('birthDate', e.target.value, { shouldValidate: true })} />
          </FormField>

          <FormField className="pin-field" label="3. НОМЕР ТЕЛЕФОНА" labelClassName="pin-label" error={errors.phone?.message}>
            <Controller control={control} name="phone" rules={{ validate: validators.phone }}
              render={({ field }) => (
                <InputMask className="inf-input" type="tel" placeholder="+7 (___) ___-__-__"
                  mask={PHONE_MASK_OPTIONS.mask} replacement={PHONE_MASK_OPTIONS.replacement}
                  value={field.value} onChange={field.onChange} />
              )} />
          </FormField>

          <FormField className="pin-field" label="4. СЕРИЯ И НОМЕР ПАСПОРТА" labelClassName="pin-label" error={errors.passport?.message}>
            <Controller control={control} name="passport" rules={{ validate: validators.passport }}
              render={({ field }) => (
                <InputMask className="inf-input" inputMode="numeric" placeholder="0000 000000"
                  mask={PASSPORT_MASK.mask} replacement={PASSPORT_MASK.replacement}
                  value={field.value} onChange={field.onChange} />
              )} />
          </FormField>

          <FormField className="pin-field" label="5. СНИЛС" labelClassName="pin-label" error={errors.snils?.message}>
            <Controller control={control} name="snils" rules={{ validate: validators.snils }}
              render={({ field }) => (
                <InputMask className="inf-input" inputMode="numeric" placeholder="000-000-000 00"
                  mask={SNILS_MASK.mask} replacement={SNILS_MASK.replacement}
                  value={field.value} onChange={field.onChange} />
              )} />
          </FormField>

          <FormField className="pin-field" label="6. ИНН" labelClassName="pin-label" error={errors.inn?.message}>
            <Input className="inf-input" inputMode="numeric" maxLength={12} aria-invalid={!!errors.inn}
              {...register('inn', {
                validate: validators.inn,
                onChange: e => { e.target.value = e.target.value.replace(/\D/g, '').slice(0, 12); },
              })} />
          </FormField>

          <FormField className="pin-field" label="7. ГОРОД ОТПРАВЛЕНИЯ" labelClassName="pin-label" error={errors.departureCity?.message}>
            <input type="hidden" {...register('departureCity', { validate: validators.departureCity })} />
            <CitySelect value={v.departureCity}
              onChange={val => setValue('departureCity', val, { shouldValidate: true })} />
          </FormField>

          <FormField className="pin-field" label="8. ГОРОД ПРИБЫТИЯ" labelClassName="pin-label">
            <input type="hidden" {...register('arrivalCity')} />
            <Input className="inf-input inf-input--locked" value="Москва" readOnly tabIndex={-1}/>
          </FormField>

          <FormField className="pin-field" label="9. ЖЕЛАЕМАЯ ДАТА" labelClassName="pin-label" error={errors.desiredDate?.message}>
            <input type="hidden" {...register('desiredDate', { validate: validators.desiredDate })} />
            <DateInput className="inf-input" name="desiredDate" min={todayStr} value={v.desiredDate}
              onChange={e => setValue('desiredDate', e.target.value, { shouldValidate: true })} />
          </FormField>

          <FormField className="pin-field" label="10. ЗДОРОВЬЕ" labelClassName="pin-label" error={errors.health?.message}>
            <Input className="inf-input" maxLength={255} aria-invalid={!!errors.health}
              {...register('health', { validate: validators.health })} />
          </FormField>

          <FormField className="pin-field" label="11. ВОЕННЫЙ БИЛЕТ" labelClassName="pin-label" error={errors.militaryId?.message}>
            <Controller control={control} name="militaryId" rules={{ validate: validators.militaryId }}
              render={({ field }) => (
                <Select name="militaryId" placeholder="Выберите" options={yesNoOpts('Есть')}
                  value={field.value} onChange={field.onChange} />
              )} />
          </FormField>

          <FormField className="pin-field" label="12. СУДИМОСТИ: ДА/НЕТ, СТАТЬИ" labelClassName="pin-label" error={errors.criminalRecord?.message}>
            <Input className="inf-input" maxLength={255} aria-invalid={!!errors.criminalRecord}
              {...register('criminalRecord', { validate: validators.criminalRecord })} />
          </FormField>

          <FormField className="pin-field" label="13. ОТНОШЕНИЕ НУЖНО" labelClassName="pin-label" error={errors.relationNeeded?.message}>
            <Controller control={control} name="relationNeeded" rules={{ validate: validators.relationNeeded }}
              render={({ field }) => (
                <Select name="relationNeeded" placeholder="Выберите" options={yesNoOpts('Да')}
                  value={field.value} onChange={field.onChange} />
              )} />
          </FormField>
        </div>

        {/* ── Блок B ── */}
        <div className="pin-relation" aria-live="polite">
          {showRelation && (
            <>
              <div className="pin-block-title">Анкета на отношение в Новую Москву</div>
              <div className="pin-grid">
                <FormField className="pin-field" label="1. ФИО" labelClassName="pin-label" error={errors.bFullName?.message}>
                  <Input className="inf-input" aria-invalid={!!errors.bFullName}
                    {...register('bFullName', {
                      validate: validators.notEmpty,
                      onChange: () => { bDirty.current.fullName = true; },
                    })} />
                </FormField>

                <FormField className="pin-field" label="2. СЕМЕЙНОЕ ПОЛОЖЕНИЕ" labelClassName="pin-label" error={errors.bMaritalStatus?.message}>
                  <Input className="inf-input" aria-invalid={!!errors.bMaritalStatus}
                    {...register('bMaritalStatus', { validate: validators.notEmpty })} />
                </FormField>

                <FormField className="pin-field" label="3. ОБРАЗОВАНИЕ" labelClassName="pin-label" error={errors.bEducation?.message}>
                  <Input className="inf-input" aria-invalid={!!errors.bEducation}
                    {...register('bEducation', { validate: validators.notEmpty })} />
                </FormField>

                <FormField className="pin-field" label="4. СУДИМОСТИ" labelClassName="pin-label" error={errors.bCriminalRecord?.message}>
                  <Input className="inf-input" aria-invalid={!!errors.bCriminalRecord}
                    {...register('bCriminalRecord', {
                      validate: validators.notEmpty,
                      onChange: () => { bDirty.current.criminal = true; },
                    })} />
                </FormField>

                <FormField className="pin-field" label="5. НОМЕР ТЕЛЕФОНА" labelClassName="pin-label" error={errors.bPhone?.message}>
                  <Controller control={control} name="bPhone" rules={{ validate: validators.phone }}
                    render={({ field }) => (
                      <InputMask className="inf-input" type="tel" placeholder="+7 (___) ___-__-__"
                        mask={PHONE_MASK_OPTIONS.mask} replacement={PHONE_MASK_OPTIONS.replacement}
                        value={field.value}
                        onChange={e => { bDirty.current.phone = true; field.onChange(e); }} />
                    )} />
                </FormField>

                <FormField className="pin-field" label="6. НА КОГО ОТНОШЕНИЕ" labelClassName="pin-label" error={errors.bDepartment?.message}>
                  <Controller control={control} name="bDepartment" rules={{ validate: validators.select }}
                    render={({ field }) => (
                      <Select name="bDepartment"
                        placeholder={departmentsEmpty ? 'Нет доступных подразделений' : 'Выберите подразделение'}
                        options={departmentOptions} disabled={departmentsEmpty}
                        value={field.value} onChange={field.onChange} />
                    )} />
                </FormField>
              </div>
            </>
          )}
        </div>

        {/* ── Предпросмотр ── */}
        <div className="pin-preview" aria-live="polite">
          <div className="pin-preview-cap">БУДЕТ СОЗДАНА ЗАДАЧА:</div>
          <div className={`pin-preview-headline${preview.ready ? ' pin-preview-ready' : ''}`}>{preview.headline}</div>
          <div className="pin-preview-text">{preview.showcase}</div>
          {preview.relation && <div className="pin-preview-text">{preview.relation}</div>}
          {preview.issues.length > 0 && (
            <ul className="pin-preview-issues">
              {preview.issues.map(issue => <li key={issue}>{issue}</li>)}
            </ul>
          )}
          <div className="pin-preview-foot">Исполнитель: {ownerName || 'Админ'} · срок: сегодня, весь день</div>
        </div>
      </div>

      <div className="cm-r-actions">
        <button className="btn-save" type="submit" disabled={isSaving || !canSubmit}>
          {isSaving ? 'Создание…' : 'Создать задачу'}
        </button>
        <button className="btn-can" type="button" onClick={onCancel} disabled={isSaving}>Отмена</button>
      </div>
    </form>
  );
};

export { PinMoscowForm };
